
import React, { memo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import StatsCardSkeleton from "./StatsCardSkeleton";
import { IconName, getIconComponent } from "./icons/StatsIcons";
import { formatValue } from "@/utils/formatters";

interface StatsCardProps {
  label: string;
  value: string | number;
  description: string;
  icon: IconName;
  loading?: boolean;
  navigateTo?: string;
  periodData?: {
    monthly?: number;
    annual?: number;
  };
  statusData?: {
    pending?: number;
    partial?: number;
  };
}

const cardVariants = {
  initial: { opacity: 0, y: 20, scale: 0.95 },
  animate: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: { duration: 0.4, ease: "easeOut" }
  },
  hover: { 
    y: -5,
    transition: { duration: 0.2 }
  },
  tap: { scale: 0.98 }
};

const StatsCard = memo(({ 
  label, 
  value, 
  description, 
  icon, 
  loading = false, 
  navigateTo, 
  periodData,
  statusData
}: StatsCardProps) => {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  
  const formattedValue = formatValue(value, icon === "DollarSign");
  const formattedMonthly = periodData?.monthly ? formatValue(periodData.monthly, icon === "DollarSign") : null;
  const formattedAnnual = periodData?.annual ? formatValue(periodData.annual, icon === "DollarSign") : null;

  // Enhanced 3D effect with mouse tracking - same as AdminCard
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isHovering) return;
    
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    
    // Calculate mouse position relative to card center
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    // Normalize values (-1 to 1) and limit rotation
    const normalizedX = (x / (rect.width / 2)) * 0.5; // Reduce rotation intensity
    const normalizedY = (y / (rect.height / 2)) * 0.5;
    
    setMousePosition({ x: normalizedX, y: normalizedY });
  };

  // Add special class for revenue cards
  const isRevenueCard = icon === "DollarSign";
  const cardClass = isRevenueCard ? "revenue-card" : "stats-card";

  const handleCardClick = () => {
    if (navigateTo) {
      console.log("Navigating to:", navigateTo);
      navigate(navigateTo);
    }
  };

  if (loading) {
    return <StatsCardSkeleton />;
  }

  // Display revenue status indicators if this is a revenue card
  const showRevenueStatus = icon === "DollarSign" && statusData && 
    (statusData.pending > 0 || statusData.partial > 0);

  return (
    <motion.div
      initial="initial"
      animate="animate"
      whileTap="tap"
      variants={cardVariants}
      className={cn(
        "cursor-pointer transform-gpu",
        navigateTo ? "cursor-pointer" : "cursor-default"
      )}
      onClick={handleCardClick}
    >
      <Card 
        className={`border dark:border-slate-700 rounded-lg overflow-hidden card-3d-container ${cardClass}`}
        style={{
          transform: isHovering ? 
            `perspective(1000px) rotateY(${mousePosition.x * 5}deg) rotateX(${-mousePosition.y * 5}deg) translateZ(15px)` : 
            'perspective(1000px) rotateY(0) rotateX(0) translateZ(0)',
          boxShadow: isHovering ? 
            isRevenueCard ? 
              '0 20px 40px rgba(0, 0, 0, 0.2), 0 0 30px rgba(34, 197, 94, 0.4), 0 0 60px rgba(34, 197, 94, 0.2)' : 
              '0 20px 40px rgba(0, 0, 0, 0.2), 0 0 30px rgba(59, 130, 246, 0.5), 0 0 60px rgba(59, 130, 246, 0.3)' : 
            '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          setIsHovering(false);
          setMousePosition({ x: 0, y: 0 });
        }}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-slate-600 dark:text-slate-300 text-sm font-medium">{label}</p>
              <p className="text-xl font-bold text-nalc-dark dark:text-white mt-1">{formattedValue}</p>
              <p className="text-xs text-muted-foreground mt-1">{description}</p>
            </div>
            <div className={`bg-nalc-teal/10 p-2 rounded-full dark:bg-nalc-teal/20 stats-icon ${isHovering ? 'animate-pulse' : ''}`}>
              {getIconComponent(icon)}
            </div>
          </div>
          
          {/* Revenue status indicators for Revenue card */}
          {showRevenueStatus && (
            <div className="mt-2 flex flex-wrap gap-2">
              {statusData.pending > 0 && (
                <Badge variant="pending" className="text-xs">
                  {formatValue(statusData.pending, true)} pending
                </Badge>
              )}
              {statusData.partial > 0 && (
                <Badge variant="info" className="text-xs">
                  {formatValue(statusData.partial, true)} partial
                </Badge>
              )}
            </div>
          )}
          
          {periodData && (
            <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
              {formattedMonthly && (
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-500">This Month:</span>
                  <span className="text-sm font-medium text-nalc-dark dark:text-white">{formattedMonthly}</span>
                </div>
              )}
              {formattedAnnual && (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">This Year:</span>
                  <span className="text-sm font-medium text-nalc-dark dark:text-white">{formattedAnnual}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
});

StatsCard.displayName = 'StatsCard';

export default StatsCard;
